import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, ShieldAlert, BarChart3 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SUPPORT_STATUS_STYLES = {
  open: 'text-blue-400 bg-blue-500/10 border-blue-500/20',
  in_progress: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
  resolved: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
  closed: 'text-slate-400 bg-slate-500/10 border-slate-500/20',
};

const PRIORITY_STYLES = {
  low: 'text-slate-400 bg-slate-500/10 border-slate-500/20',
  medium: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
  high: 'text-orange-400 bg-orange-500/10 border-orange-500/20',
  urgent: 'text-red-400 bg-red-500/10 border-red-500/20',
};

export const AdminSupportTab = ({ tickets, loading, stats, analytics, onReload }) => {
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [subTab, setSubTab] = useState('tickets');
  const [faqItems, setFaqItems] = useState([]);
  const [faqLoading, setFaqLoading] = useState(false);
  const [showFaqForm, setShowFaqForm] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [faqForm, setFaqForm] = useState({ question: '', answer: '', category: 'general', tags: '' });
  const [savingFaq, setSavingFaq] = useState(false);

  const filteredTickets = statusFilter === 'all' ? tickets : tickets.filter(t => t.status === statusFilter);

  const loadFaqs = async () => {
    setFaqLoading(true);
    try {
      const res = await axios.get(`${API}/support/admin/faq`);
      setFaqItems(res.data.faqs || []);
    } catch (e) {
      toast.error('Failed to load FAQs');
    } finally {
      setFaqLoading(false);
    }
  };

  useEffect(() => { if (subTab === 'faq') loadFaqs(); }, [subTab]);

  const updateStatus = async (ticketId, newStatus) => {
    try {
      await axios.put(`${API}/support/admin/tickets/${ticketId}/status`, { status: newStatus });
      toast.success(`Ticket status updated to ${newStatus}`);
      if (onReload) onReload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    }
  };

  const sendAdminReply = async (ticketId) => {
    if (!replyText.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/support/tickets/${ticketId}/reply`, { message: replyText });
      toast.success('Reply sent');
      setReplyText('');
      if (onReload) onReload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const saveFaq = async () => {
    if (!faqForm.question.trim() || !faqForm.answer.trim()) {
      toast.error('Question and answer are required');
      return;
    }
    setSavingFaq(true);
    try {
      const payload = {
        question: faqForm.question,
        answer: faqForm.answer,
        category: faqForm.category,
        tags: faqForm.tags.split(',').map(t => t.trim()).filter(Boolean),
      };
      if (editingFaq) {
        await axios.put(`${API}/support/admin/faq/${editingFaq.id}`, payload);
        toast.success('FAQ updated');
      } else {
        await axios.post(`${API}/support/admin/faq`, payload);
        toast.success('FAQ created');
      }
      setShowFaqForm(false);
      setEditingFaq(null);
      setFaqForm({ question: '', answer: '', category: 'general', tags: '' });
      loadFaqs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save FAQ');
    } finally {
      setSavingFaq(false);
    }
  };

  const deleteFaq = async (id) => {
    if (!window.confirm('Delete this FAQ entry?')) return;
    try {
      await axios.delete(`${API}/support/admin/faq/${id}`);
      toast.success('FAQ deleted');
      loadFaqs();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const togglePublish = async (faq) => {
    try {
      await axios.put(`${API}/support/admin/faq/${faq.id}`, { published: !faq.published });
      toast.success(faq.published ? 'FAQ unpublished' : 'FAQ published');
      loadFaqs();
    } catch (e) {
      toast.error('Failed to update');
    }
  };

  const startEdit = (faq) => {
    setEditingFaq(faq);
    setFaqForm({
      question: faq.question,
      answer: faq.answer,
      category: faq.category || 'general',
      tags: (faq.tags || []).join(', '),
    });
    setShowFaqForm(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-[var(--isp-body)]" />
        <span className="ml-2 text-[var(--isp-body)]">Loading...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-support-tab">
      <div className="flex gap-2 mb-2">
        {['tickets', 'faq', 'analytics'].map(t => (
          <button
            key={t}
            onClick={() => setSubTab(t)}
            className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
              subTab === t ? 'bg-white/10 text-white border-white/20' : 'text-[var(--isp-body)] border-[var(--isp-glass-border)] hover:bg-white/5'
            }`}
            data-testid={`admin-${t}-subtab`}
          >
            {t === 'tickets' ? `Tickets (${tickets.length})` : t === 'faq' ? `Knowledge Base (${faqItems.length})` : 'Deflection Analytics'}
          </button>
        ))}
      </div>

      {subTab === 'tickets' && (
        <>
          {stats && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              {[
                { label: 'Open', value: stats.open, color: 'text-blue-400' },
                { label: 'In Progress', value: stats.in_progress, color: 'text-amber-400' },
                { label: 'Resolved', value: stats.resolved, color: 'text-emerald-400' },
                { label: 'Urgent', value: stats.urgent_active, color: 'text-red-400' },
              ].map(s => (
                <div key={s.label} className="rounded-xl border border-[var(--isp-glass-border)] bg-slate-900/40 p-3 text-center">
                  <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                  <p className="text-xs text-[var(--isp-muted)]">{s.label}</p>
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-2 flex-wrap">
            {['all', 'open', 'in_progress', 'resolved', 'closed'].map(s => (
              <button key={s} onClick={() => setStatusFilter(s)} className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${statusFilter === s ? 'bg-white/10 text-white border-white/20' : 'text-[var(--isp-body)] border-[var(--isp-glass-border)] hover:bg-white/5'}`}>
                {s === 'all' ? 'All' : s.replace('_', ' ')}
              </button>
            ))}
          </div>
          {filteredTickets.length === 0 ? (
            <div className="text-center py-16 text-[var(--isp-body)]">
              <ShieldAlert className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="font-semibold text-white">No tickets found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredTickets.map(t => (
                <div key={t.id} className="rounded-xl border border-[var(--isp-glass-border)] bg-slate-900/40 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${SUPPORT_STATUS_STYLES[t.status]}`}>{t.status.replace('_', ' ')}</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${PRIORITY_STYLES[t.priority]}`}>{t.priority}</span>
                        <span className="text-[10px] text-[var(--isp-muted)]">{t.category}</span>
                      </div>
                      <h3 className="text-sm font-medium text-white">{t.subject}</h3>
                      <p className="text-xs text-[var(--isp-body)] mt-0.5">{t.user_name} ({t.user_email}) · {t.replies?.length || 0} replies</p>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <button onClick={() => setSelectedTicket(selectedTicket?.id === t.id ? null : t)} className="px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-white/10 hover:bg-white/15 border border-white/10">
                        {selectedTicket?.id === t.id ? 'Close' : 'View'}
                      </button>
                      {t.status === 'open' && <button onClick={() => updateStatus(t.id, 'in_progress')} className="px-2 py-1.5 rounded-lg text-xs text-amber-400 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/20">Start</button>}
                      {t.status === 'in_progress' && <button onClick={() => updateStatus(t.id, 'resolved')} className="px-2 py-1.5 rounded-lg text-xs text-emerald-400 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/20">Resolve</button>}
                      {t.status !== 'closed' && <button onClick={() => updateStatus(t.id, 'closed')} className="px-2 py-1.5 rounded-lg text-xs text-slate-400 bg-slate-500/10 hover:bg-slate-500/20 border border-slate-500/20">Close</button>}
                    </div>
                  </div>
                  {selectedTicket?.id === t.id && (
                    <div className="mt-3 pt-3 border-t border-[var(--isp-glass-border)] space-y-3">
                      <div className="p-3 rounded-lg bg-white/[0.02] text-sm text-[var(--isp-body)] whitespace-pre-wrap">{t.description}</div>
                      {t.replies?.map((r) => (
                        <div key={r.id} className={`p-3 rounded-lg text-sm ${r.is_admin ? 'bg-violet-500/[0.06] border border-violet-500/20' : 'bg-white/[0.02] border border-[var(--isp-glass-border)]'}`}>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-medium text-white">{r.is_admin ? 'Admin' : r.user_name}</span>
                            {r.is_admin && <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-500/20 text-violet-300">Staff</span>}
                          </div>
                          <p className="text-[var(--isp-body)] whitespace-pre-wrap">{r.message}</p>
                        </div>
                      ))}
                      <div className="flex gap-2">
                        <input value={replyText} onChange={e => setReplyText(e.target.value)} placeholder="Type admin reply..." className="flex-1 rounded-lg bg-transparent border border-[var(--isp-glass-border)] px-3 py-2 text-sm text-white placeholder:text-white/30 outline-none" data-testid={`admin-reply-input-${t.id}`} />
                        <button onClick={() => sendAdminReply(t.id)} disabled={sending || !replyText.trim()} className="px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-sm font-medium disabled:opacity-50" data-testid={`admin-reply-send-${t.id}`}>{sending ? '...' : 'Reply'}</button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {subTab === 'faq' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-[var(--isp-body)]">{faqItems.length} FAQ entries</p>
            <button onClick={() => { setShowFaqForm(true); setEditingFaq(null); setFaqForm({ question: '', answer: '', category: 'general', tags: '' }); }} className="px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-sm font-medium" data-testid="admin-add-faq-btn">+ Add FAQ</button>
          </div>
          {showFaqForm && (
            <div className="rounded-xl border border-violet-500/20 bg-violet-500/[0.04] p-4 space-y-3">
              <h4 className="text-sm font-semibold text-white">{editingFaq ? 'Edit FAQ' : 'New FAQ Entry'}</h4>
              <input value={faqForm.question} onChange={e => setFaqForm(f => ({ ...f, question: e.target.value }))} placeholder="Question" className="w-full rounded-lg bg-transparent border border-[var(--isp-glass-border)] px-3 py-2 text-sm text-white placeholder:text-white/30 outline-none" data-testid="admin-faq-question-input" />
              <textarea value={faqForm.answer} onChange={e => setFaqForm(f => ({ ...f, answer: e.target.value }))} placeholder="Answer" rows={4} className="w-full rounded-lg bg-transparent border border-[var(--isp-glass-border)] px-3 py-2 text-sm text-white placeholder:text-white/30 outline-none resize-none" data-testid="admin-faq-answer-input" />
              <div className="grid grid-cols-2 gap-3">
                <select value={faqForm.category} onChange={e => setFaqForm(f => ({ ...f, category: e.target.value }))} className="rounded-lg bg-transparent border border-[var(--isp-glass-border)] px-3 py-2 text-sm text-white outline-none">
                  {['getting_started','account','billing','lectures','ai_features','study_tools','technical','general'].map(c => <option key={c} value={c}>{c.replace('_', ' ')}</option>)}
                </select>
                <input value={faqForm.tags} onChange={e => setFaqForm(f => ({ ...f, tags: e.target.value }))} placeholder="Tags (comma separated)" className="rounded-lg bg-transparent border border-[var(--isp-glass-border)] px-3 py-2 text-sm text-white placeholder:text-white/30 outline-none" />
              </div>
              <div className="flex gap-2">
                <button onClick={saveFaq} disabled={savingFaq} className="px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-sm font-medium disabled:opacity-50" data-testid="admin-faq-save-btn">{savingFaq ? 'Saving...' : editingFaq ? 'Update' : 'Create'}</button>
                <button onClick={() => { setShowFaqForm(false); setEditingFaq(null); }} className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-white text-sm border border-white/10">Cancel</button>
              </div>
            </div>
          )}
          {faqLoading ? (
            <div className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin inline-block text-[var(--isp-body)]" /></div>
          ) : faqItems.length === 0 ? (
            <div className="text-center py-12 text-[var(--isp-body)]"><p className="font-semibold text-white">No FAQ entries</p></div>
          ) : (
            <div className="space-y-2">
              {faqItems.map(faq => (
                <div key={faq.id} className="rounded-xl border border-[var(--isp-glass-border)] bg-slate-900/40 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${faq.published ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20' : 'text-slate-400 bg-slate-500/10 border-slate-500/20'}`}>{faq.published ? 'Published' : 'Draft'}</span>
                        <span className="text-[10px] text-[var(--isp-muted)]">{faq.category?.replace('_', ' ')}</span>
                      </div>
                      <h4 className="text-sm font-medium text-white">{faq.question}</h4>
                      <p className="text-xs text-[var(--isp-body)] mt-0.5 line-clamp-2">{faq.answer}</p>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <button onClick={() => startEdit(faq)} className="px-2 py-1.5 rounded-lg text-xs text-blue-400 bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/20">Edit</button>
                      <button onClick={() => togglePublish(faq)} className="px-2 py-1.5 rounded-lg text-xs text-amber-400 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/20">{faq.published ? 'Hide' : 'Show'}</button>
                      <button onClick={() => deleteFaq(faq.id)} className="px-2 py-1.5 rounded-lg text-xs text-red-400 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20">Del</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {subTab === 'analytics' && (
        <div className="space-y-4" data-testid="admin-deflection-analytics">
          {analytics ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: 'Deflection Rate', value: `${analytics.deflection_rate}%`, color: 'text-emerald-400' },
                  { label: 'Tickets Saved', value: analytics.deflections, color: 'text-cyan-400' },
                  { label: 'Suggestions Shown', value: analytics.suggestions_shown, color: 'text-blue-400' },
                  { label: 'Tickets Created', value: analytics.tickets_created, color: 'text-amber-400' },
                ].map(s => (
                  <div key={s.label} className="rounded-xl border border-[var(--isp-glass-border)] bg-slate-900/40 p-4 text-center">
                    <p className={`text-3xl font-bold ${s.color}`}>{s.value}</p>
                    <p className="text-xs text-[var(--isp-muted)] mt-1">{s.label}</p>
                  </div>
                ))}
              </div>
              <div className="rounded-xl border border-[var(--isp-glass-border)] bg-slate-900/40 p-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium text-white">Ticket Deflection (last {analytics.period_days} days)</h4>
                  <span className="text-xs text-emerald-400 font-medium">{analytics.deflection_rate}% resolved by FAQ</span>
                </div>
                <div className="w-full h-3 bg-slate-800 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-full transition-all duration-500" style={{ width: `${Math.min(analytics.deflection_rate, 100)}%` }} />
                </div>
              </div>
              {analytics.top_deflecting_faqs?.length > 0 && (
                <div className="rounded-xl border border-[var(--isp-glass-border)] bg-slate-900/40 p-4">
                  <h4 className="text-sm font-medium text-white mb-3">Top Deflecting FAQ Articles</h4>
                  <div className="space-y-2">
                    {analytics.top_deflecting_faqs.map((faq) => (
                      <div key={faq.id} className="flex items-center gap-3 p-2 rounded-lg bg-white/[0.02] border border-[var(--isp-glass-border)]">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-white truncate">{faq.question}</p>
                        </div>
                        <span className="text-cyan-400 text-xs font-medium">{faq.deflections} saved</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-16 text-[var(--isp-body)]">
              <BarChart3 className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="font-semibold text-white">No analytics data yet</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
